import React, { useCallback, useEffect, useState } from "react";

import { CircularProgress, Grid } from "@mui/material";
import { Stack } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { FiUpload } from "react-icons/fi";
import { VscFilePdf } from "react-icons/vsc";
import { Item } from "semantic-ui-react";
import * as yup from "yup";

import useYupValidation from "../../../../../hooks/useYupValidation";
import { Button, Input } from "../../../../Form";
import Modal from "../../../../Generic/Modal";
import { NotificationToast } from "../../../../Generic/Toast/NotificationToast";
import useStyles from "./styles";

export interface FileData {
  uri: string;
  fileName: string;
  fileType: string;
}

interface Props {
  open: boolean;
  isLoading: boolean;
  onCloseModal: () => void;
  onUploadDocument: (url: string, name: string, callbackFunction?: () => void) => void;
  onUploadMultipleDocuments?: (
    files: FileData[],
    name: string,
    callbackFunction?: () => void
  ) => void;
  loadingUpload?: boolean;
}
interface UploadFile {
  name: string;
  files: File[];
}
const MAX_SIZE = 2 * 1024 * 1024;

const DEFAULT_FIELDS_VALUES: UploadFile = { name: "", files: [] };

const UploadDocumentModal = (props: Props) => {
  const {
    open,
    isLoading,
    onCloseModal,
    onUploadDocument,
    onUploadMultipleDocuments,
    loadingUpload,
  } = props;

  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [fields, setFields] = useState<UploadFile>(DEFAULT_FIELDS_VALUES);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const [, setTryAgainButtonIsDisable] = useState<boolean>(true);

  const onCloseThisModal = useCallback(() => {
    onCloseModal();
    setFields(DEFAULT_FIELDS_VALUES);
    setErrors([]);
  }, []);

  const schema = yup.object().shape({
    name: yup.string().required(translate("thisFieldIsRequired")),
  });
  const debounce = useCallback(() => {
    if (!loadingUpload) return setTimeout(() => {});

    return setTimeout(() => {
      setTryAgainButtonIsDisable(false);
    }, 5000);
  }, [loadingUpload]);

  useEffect(() => {
    debounce();

    return () => clearTimeout(debounce());
  }, [loadingUpload, debounce]);

  const validation = () => {
    try {
      schema.validateSync(
        {
          name: fields?.name,
        },
        { abortEarly: false }
      );

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  const onDropFile = (files: File[]) => {
    if (!files || !files.length) return;

    const validFiles: File[] = [];

    for (const file of files) {
      const splittedFileName = file.name.split(".");
      const extensionFromName = splittedFileName[splittedFileName.length - 1];

      if (file?.size && file?.size > MAX_SIZE) {
        NotificationToast.error(translate("theFileMustBeLessThan2mbInSize"));
        continue;
      }

      if (extensionFromName === "pdf") {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      setFields((prev) => ({ ...prev, files: [...prev.files, ...validFiles] }));
    }
  };

  const handleRemoveFile = (index: number) => {
    setFields((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: onDropFile,
    accept: ["application/pdf"],
    multiple: true,
  });

  const readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        const uri = reader.result?.toString();
        if (uri) {
          resolve(uri);
        } else {
          reject(new Error("Failed to read file"));
        }
      });
      reader.addEventListener("error", () => reject(new Error("Failed to read file")));
      reader.readAsDataURL(file);
    });
  };

  const onConfirmUpload = async () => {
    if (!validation()) return;
    if (!fields.files.length) return;

    if (fields.files.length === 1) {
      const uri = await readFileAsDataURL(fields.files[0]);
      onUploadDocument(uri, fields.name, onCloseThisModal);
    } else if (onUploadMultipleDocuments) {
      const filePromises = fields.files.map(async (file) => {
        const uri = await readFileAsDataURL(file);
        const splittedFileName = file.name.split(".");
        const extensionFromName = splittedFileName[splittedFileName.length - 1];
        return {
          uri,
          fileName: file.name,
          fileType: extensionFromName,
        } as FileData;
      });

      const filesData = await Promise.all(filePromises);
      onUploadMultipleDocuments(filesData, fields.name, onCloseThisModal);
    }
  };

  const renderDropzone = () => (
    <div {...getRootProps()} className={classes.dropzone}>
      <input {...getInputProps()} />
      <Stack justifyContent={"center"} alignItems={"center"} className={classes.button}>
        <Item className={classes.textButton}>
          <FiUpload size={24} />
        </Item>
        <Item className={classes.textButtonInfo}>{`${translate("maxSize")} ${translate(
          "2Mb"
        )}`}</Item>
        <Item className={classes.textButtonInfo}>
          {translate("formats")}:{" "}
          <span className={classes.textButtonFormat}>{translate("pdf")}</span>
        </Item>
      </Stack>
    </div>
  );

  const showFiles = () => {
    return (
      <div className={classes.filesList}>
        {fields.files.map((file, index) => (
          <div key={index} className={classes.showFile}>
            <div className={classes.showFileLeft}>
              <VscFilePdf size={16} className={classes.redColor} />
              <span className={classes.filename}>{file.name}</span>
            </div>

            <Button
              ghost={true}
              startIcon={<AiOutlineCloseCircle size={20} className={classes.redColor} />}
              onClick={() => handleRemoveFile(index)}
            ></Button>
          </div>
        ))}
      </div>
    );
  };

  const renderInputs = () => {
    return (
      <Grid container spacing={4} direction={"row"}>
        <Grid item xs={12} sm={12}>
          <Input
            disabled={isLoading}
            name="name"
            label={translate("name")}
            placeholder={"Ex: Admin"}
            error={fieldHasError("name")}
            errorMessage={getErrorByField("name")}
            value={fields.name}
            onChange={(event) => setFields((prev) => ({ ...prev, name: event.target.value }))}
            required
          />
        </Grid>

        <Grid item sm={12}>
          {renderDropzone()}
          {fields.files.length > 0 && showFiles()}
        </Grid>
      </Grid>
    );
  };
  const renderContentLoading = () => {
    return (
      <div className={classes.loadingMainDiv}>
        <div className={classes.loadingContainer}>
          <span className={classes.loadingTitle}>
            {translate("weAreLoadingTheUploadedContentPleaseWait")}
          </span>

          <CircularProgress className={classes.loadingProgress} />
          <div></div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      className={!loadingUpload ? classes.uploadModal : classes.loadingModal}
      open={open}
      showActionButtons={!loadingUpload}
      actionButtonProps={{
        onCancel: onCloseThisModal,
        onSave: onConfirmUpload,
        textSave: translate("add"),
        loading: loadingUpload,
        cancelDisabled: loadingUpload,
        saveDisabled: !fields.name || !fields.files.length,
      }}
      title={!loadingUpload ? translate("newDocument") : ""}
      handleClose={!loadingUpload ? onCloseThisModal : undefined}
      showModalFooter={!loadingUpload}
    >
      {!loadingUpload ? renderInputs() : renderContentLoading()}
    </Modal>
  );
};

export default UploadDocumentModal;
